import { useTranslation } from "@privacy/translations";

import {
  prefetchPersonalDataControllerById,
  prefetchPersonalDataControllerForm,
  prefetchPersonalDataControllersGridView,
  useGetPersonalDataControllerForm,
  useGetPersonalDataControllersGridView,
  personalDataControllersDisplayListQueryOptions,
} from "../../../api/personalDataControllers";
import { DynamicGrid } from "../../../dynamicGrid";
import { useGetUsersFromCurrentOrganisation } from "../../../api/users";
import { TenantIcon2 } from "../../../shared/icons";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../../../shared/usePrivacyDocumentTitle";
import { EmptyState } from "../../../shared/EmptyState";
import { selectPartnerOptionsQueryOptions } from "../../../api/partners";
import type { PersonalDataControllersGridView } from "../../../api/personalDataControllers.types";
import { PersonalDataControllersGridToolbar } from "./PersonalDataControllersGridToolbar";
import {
  PersonalDataControllerActionMenu,
  PersonalDataControllersInlineFormDrawer,
} from "./PersonalDataControllersDrawer";
import { useQuery } from "@tanstack/react-query";

const PersonalDataControllersGridPage = () => {
  usePrivacyDocumentTitle("Sidebar.PersonalDataControllers");
  const { t } = useTranslation();
  const personalDataControllersGridView = useGetPersonalDataControllersGridView();
  const personalDataControllerFormQueryResult = useGetPersonalDataControllerForm();
  const personalDataControllersQueryResult = useQuery(personalDataControllersDisplayListQueryOptions());
  const usersQueryResult = useGetUsersFromCurrentOrganisation();
  const partnersQueryResult = useQuery(selectPartnerOptionsQueryOptions());
  const { navigate, paths } = usePrivacyNavigate();

  return (
    <DynamicGrid.Grid
      rowsData={personalDataControllersGridView.data?.personalDataControllers}
      form={personalDataControllerFormQueryResult.data}
      getRowId={getRowId}
      asyncQuestionCardData={{
        usersQueryData: usersQueryResult.data,
        personalDataControllersQueryData: personalDataControllersQueryResult.data,
        partnersQueryData: partnersQueryResult.data,
      }}
      mainColumnProps={{
        headerName: t("PersonalDataControllersGrid.NameHeader"),
        renderLabel: ({ name }) => name,
        renderIcon: ({ iconProps }) => <TenantIcon2 {...iconProps} />,
      }}
      getColumns={questionCardColumns => questionCardColumns}
      onRowSelect={selected => navigate(paths.pdcInline(selected))}
      Toolbar={PersonalDataControllersGridToolbar}
      Footer={PersonalDataControllersGridFooter}
      NoRowsOverlay={NoPersonalDataControllerOverlay}
      texts={{ searchFieldPlaceholder: t("PersonalDataControllersGrid.SearchFieldLabel") }}
      renderActionMenu={({ row, selectedRowId }) => (
        <PersonalDataControllerActionMenu
          personalDataControllerId={row.id}
          personalDataControllerName={row.name}
          onDeleted={() => {
            if (selectedRowId === row.id) navigate(paths.pdcGrid());
          }}
        />
      )}
    />
  );
};

type Row = PersonalDataControllersGridView["personalDataControllers"][number];
const getRowId = (row: Row) => row.id;

const PersonalDataControllersGridFooter = () => (
  <DynamicGrid.Footer numberOfRowsTextKey="PersonalDataControllersGrid.NumberOfRows" />
);

const NoPersonalDataControllerOverlay = () => {
  return <EmptyState labelKey="PersonalDataControllersGrid.NoRows" />;
};

export const personalDataControllersGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <PersonalDataControllersGridPage />,
  rowDrawerRoutes: [{ index: true, element: <PersonalDataControllersInlineFormDrawer /> }],
  gridPrefetcher: () => prefetchPersonalDataControllersGridViewAndForm(),
  rowDrawerDefaultPrefetcher: ({ rowId }) => prefetchPersonalDataControllerById(rowId),
});

export const prefetchPersonalDataControllersGridViewAndForm = () => {
  prefetchPersonalDataControllersGridView();
  prefetchPersonalDataControllerForm();
};
